import React from 'react';
import { Check, Star, Crown, Sparkles, X, Cpu } from 'lucide-react';
import { cn } from '../lib/utils';

const packageIcons = {
  ultra: Cpu,
  essential: Star,
  platino: Crown,
  premium: Sparkles
};

const packageColors = {
  ultra: 'from-cyan-500 to-teal-700',
  essential: 'from-blue-600 to-blue-800',
  platino: 'from-purple-500 to-purple-800',
  premium: 'from-amber-500 to-amber-700'
};

function PackageCard({ pkg, isSelected, onSelect, formatPrice, currency }) {
  const Icon = packageIcons[pkg.id] || Star;
  const isEssential = pkg.id === 'essential';

  return (
    <button
      onClick={() => onSelect(pkg.id)}
      data-testid={`btn-package-${pkg.id}`}
      className={cn(
        "relative flex flex-col p-4 rounded-xl border text-left transition-all duration-300 group",
        "bg-gradient-to-b from-[#1a1f50]/80 to-[#080c2d]/90",
        isSelected
          ? "border-[#aa2aff] shadow-[0_0_25px_rgba(170,42,255,0.35)] scale-[1.02] z-10"
          : "border-white/10 hover:border-[#aa2aff]/50 hover:shadow-lg",
        isEssential && !isSelected && "border-[#aa2aff]/30"
      )}
    >
      {isEssential && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 bg-gradient-to-r from-[#aa2aff] to-[#7012b2] text-white text-[10px] font-bold rounded-full shadow-lg whitespace-nowrap">
          MAS POPULAR
        </div>
      )}

      <div className="flex items-center gap-2 mb-2">
        <div className={cn("p-1.5 rounded-lg bg-gradient-to-br", packageColors[pkg.id] || 'from-gray-600 to-gray-800')}>
          <Icon className="w-4 h-4 text-white" />
        </div>
        <h3 className={cn("text-base font-bold transition-colors", isSelected ? "text-[#aa2aff]" : "text-white group-hover:text-[#aa2aff]")}>
          {pkg.name}
        </h3>
      </div>

      <div className="mb-2">
        <span className="text-xl font-bold text-white">{formatPrice(pkg.base_price)}</span>
        <span className="text-slate-400 text-xs ml-1">{currency}/hr</span>
      </div>

      <p className="text-slate-400 text-xs mb-3 line-clamp-2">{pkg.description}</p>

      <ul className="space-y-1.5 flex-grow">
        {pkg.features.slice(0, 3).map((feature, idx) => (
          <li key={idx} className="flex items-start gap-1.5 text-xs text-slate-300">
            <Check className="w-3 h-3 text-[#aa2aff] mt-0.5 flex-shrink-0" />
            <span>{feature}</span>
          </li>
        ))}
        {pkg.features.length > 3 && (
          <li className="text-xs text-[#aa2aff]">+{pkg.features.length - 3} mas...</li>
        )}
      </ul>

      <div className={cn(
        "mt-3 py-1.5 text-center text-xs font-semibold rounded-lg transition-all",
        isSelected ? "bg-[#aa2aff] text-white" : "bg-white/5 text-slate-400 group-hover:bg-white/10"
      )}>
        {isSelected ? 'Seleccionado' : 'Seleccionar'}
      </div>
    </button>
  );
}

export function PackageSelector({ packages, selectedPackage, onSelect, formatPrice, currency = 'MXN' }) {
  const audiobooks = packages.filter(p => p.id === 'ultra' || p.id === 'essential');
  const videobooks = packages.filter(p => p.id === 'platino' || p.id === 'premium');

  // Sort within each group
  const sortedAudio = audiobooks.sort((a, b) => {
    const order = { ultra: 0, essential: 1 };
    return (order[a.id] ?? 99) - (order[b.id] ?? 99);
  });
  const sortedVideo = videobooks.sort((a, b) => {
    const order = { platino: 0, premium: 1 };
    return (order[a.id] ?? 99) - (order[b.id] ?? 99);
  });

  return (
    <div className="space-y-5">
      {/* Deselect option */}
      {selectedPackage && (
        <button
          onClick={() => onSelect(null)}
          data-testid="btn-package-none"
          className="w-full flex items-center justify-center gap-2 p-2.5 rounded-lg border border-slate-600 text-slate-400 hover:border-red-500/50 hover:text-red-400 hover:bg-red-500/10 transition-all text-sm"
        >
          <X className="w-4 h-4" />
          <span>Sin Audiolibro (quitar seleccion)</span>
        </button>
      )}

      {/* Row 1: Audiolibros */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <div className="h-px flex-1 bg-gradient-to-r from-cyan-500/40 to-transparent" />
          <span className="text-xs font-semibold text-cyan-400 uppercase tracking-wider">Audiolibros</span>
          <div className="h-px flex-1 bg-gradient-to-l from-blue-500/40 to-transparent" />
        </div>
        <div className="grid grid-cols-2 gap-3" data-testid="package-selector-audio">
          {sortedAudio.map(pkg => (
            <PackageCard key={pkg.id} pkg={pkg} isSelected={selectedPackage === pkg.id} onSelect={onSelect} formatPrice={formatPrice} currency={currency} />
          ))}
        </div>
      </div>

      {/* Row 2: Videolibros + Distribución */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <div className="h-px flex-1 bg-gradient-to-r from-purple-500/40 to-transparent" />
          <span className="text-xs font-semibold text-purple-400 uppercase tracking-wider">Videolibros + Distribucion Multiplataforma</span>
          <div className="h-px flex-1 bg-gradient-to-l from-amber-500/40 to-transparent" />
        </div>
        <div className="grid grid-cols-2 gap-3" data-testid="package-selector-video">
          {sortedVideo.map(pkg => (
            <PackageCard key={pkg.id} pkg={pkg} isSelected={selectedPackage === pkg.id} onSelect={onSelect} formatPrice={formatPrice} currency={currency} />
          ))}
        </div>
      </div>
    </div>
  );
}

export default PackageSelector;
